import React from 'react';
import { motion } from 'framer-motion';
import { FiCode, FiSmartphone, FiTarget, FiUsers } from 'react-icons/fi';
import RevealHeading from '../common/RevealHeading';
import useParallax from '../common/useParallax';

const PremiumAbout = () => {
  const { ref: parallaxRef, y: parallaxY } = useParallax(60);
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const bentoItems = [
    {
      title: 'AI Engineer',
      subtitle: 'ML/DL/CV/NLP',
      icon: FiCode,
      span: 'col-span-1 sm:col-span-2 lg:col-span-2',
      gradient: 'from-primary-500 to-accent-500',
    },
    {
      title: 'Full Stack Developer',
      subtitle: 'React • Node.js • Cloud',
      icon: FiCode,
      span: 'col-span-1 sm:col-span-1 lg:col-span-1',
      gradient: 'from-accent-500 to-primary-500',
    },
    {
      title: 'Flutter Developer',
      subtitle: 'Cross-platform Apps',
      icon: FiSmartphone,
      span: 'col-span-1 sm:col-span-1 lg:col-span-1',
      gradient: 'from-primary-500 via-accent-500 to-primary-500',
    },
    {
      title: 'Startup Founder',
      subtitle: 'Zynqora Edge CTO',
      icon: FiTarget,
      span: 'col-span-1 sm:col-span-2 lg:col-span-2',
      gradient: 'from-accent-500 to-primary-500',
    },
    {
      title: 'Problem Solver',
      subtitle: 'Architecture & Delivery',
      icon: FiUsers,
      span: 'col-span-1 sm:col-span-2 lg:col-span-2',
      gradient: 'from-primary-500 to-accent-500',
    },
  ];

  return (
    <section id="about" ref={parallaxRef} className="py-20 lg:py-28 relative overflow-hidden">
      {/* Background Elements */}
      <motion.div
        style={{ y: parallaxY }}
        animate={{ opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute -top-40 -left-40 w-80 h-80 bg-primary-500 rounded-full blur-3xl opacity-10"
      />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <RevealHeading text="About" accent="Me" className="text-4xl lg:text-5xl font-bold text-white mb-4" />
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            AI-focused developer with experience across the full stack and mobile platforms
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12"
        >
          {bentoItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={idx}
                variants={itemVariants}
                className={`${item.span} group`}
                whileHover={{ y: -10 }}
              >
                <div
                  className="relative h-48 lg:h-56 rounded-2xl overflow-hidden cursor-pointer"
                  style={{
                    perspective: '1200px',
                    transformStyle: 'preserve-3d',
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width - 0.5;
                    const y = (e.clientY - rect.top) / rect.height - 0.5;
                    e.currentTarget.style.transform = `rotateX(${y * 10}deg) rotateY(${x * 10}deg) translateZ(20px)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'rotateX(0) rotateY(0) translateZ(0)';
                  }}
                >
                  {/* Card Background */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}
                  />
                  <div className="absolute inset-0 bg-dark-800/60 backdrop-blur-sm" />

                  {/* Content */}
                  <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
                    <div className="mb-4 p-3 rounded-lg bg-primary-500/20 text-primary-500 group-hover:bg-primary-500 group-hover:text-dark-900 transition-all duration-300">
                      <Icon className="text-3xl" />
                    </div>
                    <h3 className="text-lg lg:text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-white/60 group-hover:text-white/80 transition-colors">
                      {item.subtitle}
                    </p>
                  </div>

                  {/* Border Glow */}
                  <div className="absolute inset-0 rounded-2xl border border-primary-500/0 group-hover:border-primary-500/50 transition-all duration-300" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bio Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto"
        >
          <div className="p-8 rounded-2xl bg-dark-800/30 border border-primary-500/10 hover:border-primary-500/30 transition-all duration-300">
            <h3 className="text-xl font-bold text-white mb-4">Experience</h3>
            <div className="space-y-3 text-white/70">
              <p>
                <strong className="text-primary-500">ML Engineer Intern</strong> - Developers Hub
                <br />
                <span className="text-sm">Feb – May 2024</span>
              </p>
              <p>
                <strong className="text-primary-500">Freelance Developer</strong> - Upwork
                <br />
                <span className="text-sm">2021 – Present</span>
              </p>
              <p>
                <strong className="text-primary-500">CTO</strong> -{' '}
                <a
                  href="https://zynqoraedge.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-500 hover:text-primary-500 transition-colors"
                >
                  Zynqora Edge ↗
                </a>
                <br />
                <span className="text-sm">Present</span>
              </p>
            </div>
          </div>

          <div className="p-8 rounded-2xl bg-dark-800/30 border border-primary-500/10 hover:border-primary-500/30 transition-all duration-300">
            <h3 className="text-xl font-bold text-white mb-4">Education</h3>
            <div className="space-y-3 text-white/70">
              <p>
                <strong className="text-primary-500">BS Artificial Intelligence</strong>
                <br />
                <span className="text-sm">Barani Institute of IT (PMAS)</span>
                <br />
                <span className="text-xs text-white/50">2022 - 2026</span>
              </p>
            </div>
          </div>

          <div className="p-8 rounded-2xl bg-dark-800/30 border border-accent-500/10 hover:border-accent-500/30 transition-all duration-300">
            <h3 className="text-xl font-bold text-white mb-4">Location</h3>
            <div className="space-y-3 text-white/70">
              <p>
                <strong className="text-accent-500">Based in Islamabad, Pakistan</strong>
                <br />
                <span className="text-sm">Open to remote opportunities worldwide</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PremiumAbout;
